import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useAuth } from '../context/AuthContext';
import { landlordAPI } from '../api';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Settings, Package, Building2, BarChart3, Loader2, Save, Plus,
  Edit, Users, DollarSign, TrendingUp, AlertTriangle, CheckCircle,
  Pause, Play, Eye, FileText, Trash2, Copy, Shield, Upload, X,
  ShoppingCart, Clock, CheckCheck, XCircle, Image, Download, Ban,
  MoreHorizontal, UserCog, KeyRound, Crown, LogIn, Gift, CalendarPlus, Mail,
  RotateCcw, HardDriveDownload, Database, FileUp, UploadCloud
} from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '../components/RichTextEditor';

const fmt = (n) => n != null ? `₱${Number(n).toLocaleString('en-PH', { minimumFractionDigits: 0 })}` : 'Custom';

const LandlordDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState(null);
  const [packages, setPackages] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [financial, setFinancial] = useState(null);
  
  const [editPkg, setEditPkg] = useState(null);
  const [editDialog, setEditDialog] = useState(false);

  // Template state
  const [templates, setTemplates] = useState([]);
  const [editTmpl, setEditTmpl] = useState(null);
  const [tmplDialog, setTmplDialog] = useState(false);
  const [tmplFilter, setTmplFilter] = useState('all');
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewName, setPreviewName] = useState('');
  const [uploading, setUploading] = useState(false);

  // Orders state
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState('all');
  const [reviewDialog, setReviewDialog] = useState(false);
  const [reviewOrder, setReviewOrder] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');

  // Tenant Management state
  const [tenantDetailDialog, setTenantDetailDialog] = useState(false);
  const [tenantDetail, setTenantDetail] = useState(null);
  const [tenantDetailLoading, setTenantDetailLoading] = useState(false);
  const [impersonateDialog, setImpersonateDialog] = useState(false);
  const [impersonateTenant, setImpersonateTenantState] = useState(null);
  const [makeAdminDialog, setMakeAdminDialog] = useState(false);
  const [makeAdminTenant, setMakeAdminTenant] = useState(null);
  const [makeAdminUsers, setMakeAdminUsers] = useState([]);
  const [resetLinkDialog, setResetLinkDialog] = useState(false);
  const [resetLinkTenant, setResetLinkTenant] = useState(null);
  const [resetLinkUsers, setResetLinkUsers] = useState([]);
  const [resetLinkResult, setResetLinkResult] = useState(null);
  const [extendTrialDialog, setExtendTrialDialog] = useState(false);
  const [extendTrialTenant, setExtendTrialTenant] = useState(null);
  const [extendTrialDays, setExtendTrialDays] = useState(30);
  const [grantFreeDialog, setGrantFreeDialog] = useState(false);
  const [grantFreeTenant, setGrantFreeTenant] = useState(null);
  const [suspendDialog, setSuspendDialog] = useState(false);
  const [suspendTenant, setSuspendTenantState] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTenantState, setDeleteTenantState] = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Backup & Restore state
  const [restoreDialog, setRestoreDialog] = useState(false);
  const [restoreMode, setRestoreMode] = useState('merge');
  const [backupsList, setBackupsList] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [settingsRes, pkgRes, tenantRes, finRes, tmplRes, ordersRes] = await Promise.all([
        landlordAPI.getSettings(),
        landlordAPI.getPackages(),
        landlordAPI.getTenants(),
        landlordAPI.getFinancialOverview(),
        landlordAPI.getTemplates(),
        landlordAPI.getOrders(),
      ]);
      setSettings(settingsRes.data);
      setPackages(pkgRes.data);
      setTenants(tenantRes.data);
      setFinancial(finRes.data);
      setTemplates(tmplRes.data);
      setOrders(ordersRes.data);
    } catch (err) {
      console.error('Failed to fetch landlord data:', err);
      toast.error('Failed to load landlord data');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await landlordAPI.updateSettings(settings);
      toast.success('Settings saved');
      fetchAll();
    } catch (err) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const savePackage = async () => {
    if (!editPkg) return;
    setSaving(true);
    try {
      if (editPkg.id) {
        await landlordAPI.updatePackage(editPkg.id, editPkg);
      } else {
        await landlordAPI.createPackage(editPkg);
      }
      toast.success('Package saved');
      setEditDialog(false);
      setEditPkg(null);
      fetchAll();
    } catch (err) { toast.error('Failed to save package'); }
    finally { setSaving(false); }
  };

  const togglePackageActive = async (pkg) => {
    try {
      await landlordAPI.updatePackage(pkg.id, { is_active: !pkg.is_active });
      toast.success(pkg.is_active ? 'Package deactivated' : 'Package activated');
      fetchAll();
    } catch (err) { toast.error('Failed to update'); }
  };

  const handleTenantAction = async (tenantId, action) => {
    try {
      if (action === 'suspend') await landlordAPI.suspendTenant(tenantId);
      else await landlordAPI.activateTenant(tenantId);
      toast.success(`Tenant ${action}d`);
      fetchAll();
    } catch (err) { toast.error('Failed'); }
  };

  // Tenant Management handlers
  const openTenantDetails = async (tenant) => {
    setTenantDetailLoading(true);
    setTenantDetailDialog(true);
    try {
      const res = await landlordAPI.getTenantDetails(tenant.id);
      setTenantDetail(res.data);
    } catch (err) {
      toast.error('Failed to load tenant details');
      setTenantDetailDialog(false);
    } finally {
      setTenantDetailLoading(false);
    }
  };

  const openImpersonate = (tenant) => {
    setImpersonateTenantState(tenant);
    setImpersonateDialog(true);
  };

  const handleImpersonate = async () => {
    if (!impersonateTenant) return;
    setActionLoading(true);
    try {
      const res = await landlordAPI.impersonateTenant(impersonateTenant.id);
      const { token, user: impUser, organization_name } = res.data;
      // Store the landlord's token for returning later
      const landlordToken = localStorage.getItem('token');
      localStorage.setItem('landlord_return_token', landlordToken);
      // Set the impersonation token
      localStorage.setItem('token', token);
      localStorage.setItem('impersonating', 'true');
      localStorage.setItem('impersonating_org', organization_name);
      toast.success(`Now impersonating ${impUser.full_name || impUser.email} at ${organization_name}`);
      setImpersonateDialog(false);
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to impersonate');
    } finally {
      setActionLoading(false);
    }
  };

  const openMakeAdmin = async (tenant) => {
    setMakeAdminTenant(tenant);
    setMakeAdminDialog(true);
    try {
      const res = await landlordAPI.getTenantDetails(tenant.id);
      setMakeAdminUsers(res.data.users || []);
    } catch (err) {
      toast.error('Failed to load users');
    }
  };

  const handleMakeAdmin = async (userId) => {
    setActionLoading(true);
    try {
      await landlordAPI.makeUserAdmin(userId);
      toast.success('User promoted to Platform Admin');
      setMakeAdminDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to promote user');
    } finally {
      setActionLoading(false);
    }
  };

  const openResetLink = async (tenant) => {
    setResetLinkTenant(tenant);
    setResetLinkResult(null);
    setResetLinkDialog(true);
    try {
      const res = await landlordAPI.getTenantDetails(tenant.id);
      setResetLinkUsers(res.data.users || []);
    } catch (err) {
      toast.error('Failed to load users');
    }
  };

  const handleSendResetLink = async (userId) => {
    setActionLoading(true);
    try {
      const res = await landlordAPI.sendResetLink(userId);
      setResetLinkResult(res.data);
      if (res.data.email_sent) {
        toast.success('Password reset email sent');
      } else {
        toast.info('Reset link generated (email not sent — share the link manually)');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setActionLoading(false);
    }
  };

  const openExtendTrial = (tenant) => {
    setExtendTrialTenant(tenant);
    setExtendTrialDays(30);
    setExtendTrialDialog(true);
  };

  const handleExtendTrial = async () => {
    if (!extendTrialTenant) return;
    setActionLoading(true);
    try {
      const res = await landlordAPI.extendTrial(extendTrialTenant.id, { days: extendTrialDays });
      toast.success(`Trial extended by ${extendTrialDays} days for ${res.data.organization_name}`);
      setExtendTrialDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to extend trial');
    } finally {
      setActionLoading(false);
    }
  };

  const openGrantFree = (tenant) => {
    setGrantFreeTenant(tenant);
    setGrantFreeDialog(true);
  };

  const handleGrantFree = async () => {
    if (!grantFreeTenant) return;
    setActionLoading(true);
    try {
      const res = await landlordAPI.grantFreeSubscription(grantFreeTenant.id);
      toast.success(`30-day free subscription granted to ${res.data.organization_name}`);
      setGrantFreeDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant free subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const openSuspend = (tenant) => {
    setSuspendTenantState(tenant);
    setSuspendDialog(true);
  };

  const handleSuspendConfirm = async () => {
    if (!suspendTenant) return;
    setActionLoading(true);
    try {
      const action = suspendTenant.subscription_status === 'suspended' ? 'activate' : 'suspend';
      await handleTenantAction(suspendTenant.id, action);
      setSuspendDialog(false);
    } catch (err) {
      toast.error('Failed');
    } finally {
      setActionLoading(false);
    }
  };

  const openDelete = (tenant) => {
    setDeleteTenantState(tenant);
    setDeleteConfirmName('');
    setDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTenantState) return;
    setActionLoading(true);
    try {
      await landlordAPI.deleteTenant(deleteTenantState.id);
      toast.success(`Tenant "${deleteTenantState.name}" deleted`);
      setDeleteDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete tenant');
    } finally {
      setActionLoading(false);
    }
  };

  const openEditPkg = (pkg) => {
    setEditPkg({ ...pkg, feature_flags: { ...pkg.feature_flags } });
    setEditDialog(true);
  };

  const openNewPkg = () => {
    setEditPkg({
      code: '', name: '', description: '', monthly_price_php: 0,
      annual_discount_percent: 20, included_governance_users: 10,
      included_entities: 1, max_committees: 2, sort_order: packages.length + 1,
      feature_flags: { board_meetings: true, committee_meetings: true, asm_ssm: false,
        ai_minutes: false, ai_resolutions: false, ai_certificates: false, ai_notices: false,
        elections: false, proxy_management: false, pdf_export: true, public_library: false },
    });
    setEditDialog(true);
  };

  // Backup & Restore handlers
  const fetchBackups = async () => {
    setBackupsLoading(true);
    try {
      const res = await landlordAPI.listTenantsBackups();
      setBackupsList(res.data);
    } catch (err) {
      toast.error('Failed to load backups');
    } finally {
      setBackupsLoading(false);
    }
  };

  const handleExportBackup = async () => {
    setExportLoading(true);
    try {
      const res = await landlordAPI.exportTenantsBackup(true);
      const { name } = res.data;
      // Download to computer
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Backup "${name}" downloaded & saved to system (${res.data.tenant_count} tenants, ${res.data.user_count} users)`);
    } catch (err) {
      toast.error('Failed to export backup');
    } finally {
      setExportLoading(false);
    }
  };

  const openRestore = () => {
    setRestoreMode('merge');
    setRestoreDialog(true);
    fetchBackups();
  };

  const handleRestoreFromFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a JSON backup file');
      return;
    }
    setRestoreLoading(true);
    try {
      const formData = new FormData();
      formData.append('mode', restoreMode);
      formData.append('file', file);
      const res = await landlordAPI.restoreTenantsBackup(formData);
      toast.success(`Restored ${res.data.restored_tenants} tenants, ${res.data.restored_users} users (${restoreMode} mode). Safety backup: ${res.data.safety_backup_name}`);
      setRestoreDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore from file');
    } finally {
      setRestoreLoading(false);
      e.target.value = '';
    }
  };

  const handleRestoreFromDb = async (backupId) => {
    setRestoreLoading(true);
    try {
      const formData = new FormData();
      formData.append('mode', restoreMode);
      formData.append('backup_id', backupId);
      const res = await landlordAPI.restoreTenantsBackup(formData);
      toast.success(`Restored ${res.data.restored_tenants} tenants, ${res.data.restored_users} users (${restoreMode} mode). Safety backup: ${res.data.safety_backup_name}`);
      setRestoreDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore');
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleDeleteBackup = async (backupId) => {
    try {
      await landlordAPI.deleteTenantsBackup(backupId);
      toast.success('Backup deleted');
      setBackupsList(prev => prev.filter(b => b.id !== backupId));
    } catch (err) {
      toast.error('Failed to delete backup');
    }
  };


  // Template handlers
  const TEMPLATE_TYPES = [
    { value: 'notice', label: 'Notice' },
    { value: 'agenda', label: 'Agenda' },
    { value: 'minutes', label: 'Minutes' },
    { value: 'resolution', label: 'Resolution' },
    { value: 'certificate', label: 'Certificate' },
    { value: 'other', label: 'Other' },
  ];
  const MEETING_TYPES = [
    { value: 'board', label: 'Board' },
    { value: 'committee', label: 'Committee' },
    { value: 'asm', label: 'ASM' },
    { value: 'ssm', label: 'SSM' },
  ];
  const COMMON_PLACEHOLDERS = {
    'organization.name': 'Organization Name',
    'organization.address': 'Principal Office Address',
    'meeting.title': 'Meeting Title',
    'meeting.date': 'Meeting Date',
    'meeting.time': 'Meeting Time',
    'meeting.venue': 'Meeting Venue',
    'meeting.type': 'Meeting Type',
    'meeting.body_name': 'Body Name',
    'secretary.name': 'Corporate Secretary Name',
    'chairman.name': 'Chairman Name',
    'quorum.value': 'Quorum Value',
    'resolution.number': 'Resolution Number',
    'resolution.text': 'Resolution Text',
    'resolution.title': 'Resolution Title',
    'certificate.number': 'Certificate Number',
    'current_date': 'Current Date',
  };

  const openNewTmpl = () => {
    setEditTmpl({
      name: '', template_type: 'notice', applicable_meeting_types: [],
      body_html: '', placeholders: { ...COMMON_PLACEHOLDERS },
      source: 'SEC_RECOMMENDED', is_active: true,
    });
    setTmplDialog(true);
  };

  const openEditTmpl = (tmpl) => {
    setEditTmpl({
      ...tmpl,
      placeholders: tmpl.placeholders && Object.keys(tmpl.placeholders).length > 0
        ? tmpl.placeholders : { ...COMMON_PLACEHOLDERS },
    });
    setTmplDialog(true);
  };

  const saveTmpl = async () => {
    if (!editTmpl) return;
    setSaving(true);
    try {
      if (editTmpl.id) {
        const { id, scope, created_at, created_by, updated_at, ...updates } = editTmpl;
        await landlordAPI.updateTemplate(editTmpl.id, updates);
      } else {
        await landlordAPI.createTemplate(editTmpl);
      }
      toast.success('Template saved');
      setTmplDialog(false);
      setEditTmpl(null);
      fetchAll();
    } catch (err) { toast.error('Failed to save template'); }
    finally { setSaving(false); }
  };

  const deleteTmpl = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await landlordAPI.deleteTemplate(id);
      toast.success('Template deleted');
      fetchAll();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['docx', 'html', 'htm'].includes(ext)) {
      toast.error('Only .docx and .html files are supported');
      return;
    }
    setUploading(true);
    try {
      const res = await landlordAPI.uploadTemplateFile(file);
      setEditTmpl(prev => ({ ...prev, body_html: res.data.html }));
      if (res.data.warnings?.length > 0) {
        toast.info(`Converted with ${res.data.warnings.length} warning(s)`);
      } else {
        toast.success(`File "${file.name}" converted to HTML`);
      }
    } catch (err) {
      toast.error('Failed to convert file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handlePreview = async (tmpl) => {
    try {
      const res = await landlordAPI.previewTemplate(tmpl.id);
      setPreviewHtml(res.data.rendered_html);
      setPreviewName(res.data.template_name);
      setPreviewDialog(true);
    } catch (err) {
      toast.error('Failed to generate preview');
    }
  };

  // Order handlers
  const pendingOrderCount = orders.filter(o => o.status === 'pending' || o.status === 'proof_uploaded').length;

  const openReviewOrder = (order) => {
    setReviewOrder(order);
    setReviewNotes('');
    setReviewDialog(true);
  };

  const handleApproveOrder = async () => {
    if (!reviewOrder) return;
    setSaving(true);
    try {
      await landlordAPI.approveOrder(reviewOrder.id, { notes: reviewNotes });
      toast.success(`Order ${reviewOrder.order_id} approved — subscription activated`);
      setReviewDialog(false);
      fetchAll();
    } catch (err) { toast.error('Failed to approve'); }
    finally { setSaving(false); }
  };

  const handleRejectOrder = async () => {
    if (!reviewOrder) return;
    setSaving(true);
    try {
      await landlordAPI.rejectOrder(reviewOrder.id, { notes: reviewNotes });
      toast.success(`Order ${reviewOrder.order_id} rejected`);
      setReviewDialog(false);
      fetchAll();
    } catch (err) { toast.error('Failed to reject'); }
    finally { setSaving(false); }
  };

  const filteredOrders = orderFilter === 'all' ? orders : orders.filter(o => o.status === orderFilter);

  const handleDeleteProof = async (orderId) => {
    if (!window.confirm('Delete this proof of payment file? This cannot be undone.')) return;
    try {
      await landlordAPI.deleteProof(orderId);
      toast.success('Proof of payment deleted');
      if (reviewOrder?.id === orderId) {
        setReviewOrder({ ...reviewOrder, proof_file_path: null, proof_uploaded_at: null });
      }
      fetchAll();
    } catch (err) { toast.error('Failed to delete proof'); }
  };

  const getProofUrl = (path) => path ? `${process.env.REACT_APP_BACKEND_URL}${path}` : null;
  const isProofImage = (path) => path && /\.(jpg|jpeg|png|webp)$/i.test(path);

  const filteredTemplates = tmplFilter === 'all'
    ? templates
    : templates.filter(t => t.template_type === tmplFilter);

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" /></div>;
  }

  return (
    <div className="space-y-6 overflow-x-hidden" data-testid="landlord-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-[#1A1D21]">Landlord Admin</h1>
        <p className="text-[#5A6570] mt-1">Platform management, pricing, and financial overview</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="tabs-grid-3 bg-[#F0F2F4] p-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white"><BarChart3 className="h-4 w-4 mr-1.5" />Financial</TabsTrigger>
          <TabsTrigger value="packages" className="data-[state=active]:bg-white"><Package className="h-4 w-4 mr-1.5" />Pricing</TabsTrigger>
          <TabsTrigger value="costs" className="data-[state=active]:bg-white"><DollarSign className="h-4 w-4 mr-1.5" />Costs</TabsTrigger>
          <TabsTrigger value="tenants" className="data-[state=active]:bg-white"><Building2 className="h-4 w-4 mr-1.5" />Tenants</TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-white"><FileText className="h-4 w-4 mr-1.5" />Templates</TabsTrigger>
          <TabsTrigger value="orders" className="data-[state=active]:bg-white relative">
            <ShoppingCart className="h-4 w-4 mr-1.5" />Orders
            {pendingOrderCount > 0 && (
              <span className="ml-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center">{pendingOrderCount}</span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Financial Overview */}
        <TabsContent value="overview" className="space-y-6">
          {financial && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-[#00A3A3]/30 bg-[#00A3A3]/5">
                  <CardContent className="p-4">
                    <p className="text-xs text-[#5A6570]">Total Tenants</p>
                    <p className="text-3xl font-bold text-[#00A3A3]">{financial.total_tenants}</p>
                  </CardContent>
                </Card>
                <Card className="border-[#F58A26]/30 bg-[#F58A26]/5">
                  <CardContent className="p-4">
                    <p className="text-xs text-[#5A6570]">Total ARR</p>
                    <p className="text-3xl font-bold text-[#F58A26]">{fmt(financial.total_arr)}</p>
                  </CardContent>
                </Card>
                <Card className="border-[#E2E6EA]">
                  <CardContent className="p-4">
                    <p className="text-xs text-[#5A6570]">Est. Yearly Run Cost</p>
                    <p className="text-3xl font-bold text-[#1A1D21]">{fmt(financial.estimated_yearly_run_cost)}</p>
                  </CardContent>
                </Card>
                <Card className={`border ${financial.surplus_deficit >= 0 ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
                  <CardContent className="p-4">
                    <p className="text-xs text-[#5A6570]">Surplus / Deficit</p>
                    <p className={`text-3xl font-bold ${financial.surplus_deficit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {fmt(financial.surplus_deficit)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card className="border-[#E2E6EA] overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-base">Revenue by Package</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]" data-testid="financial-table">
                    <thead>
                      <tr className="border-b border-[#E2E6EA] bg-[#F8F9FA]">
                        <th className="text-left p-3 text-xs font-medium text-[#5A6570]">Package</th>
                        <th className="text-right p-3 text-xs font-medium text-[#5A6570]">Monthly Price</th>
                        <th className="text-right p-3 text-xs font-medium text-[#5A6570]">Tenants</th>
                        <th className="text-right p-3 text-xs font-medium text-[#5A6570]">ARR</th>
                        <th className="text-right p-3 text-xs font-medium text-[#5A6570]">Break-Even</th>
                        <th className="text-center p-3 text-xs font-medium text-[#5A6570]">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {financial.package_stats.map(ps => (
                        <tr key={ps.code} className="border-b border-[#E2E6EA] last:border-0">
                          <td className="p-3 font-medium text-[#1A1D21]">{ps.name}</td>
                          <td className="p-3 text-right font-mono">{fmt(ps.monthly_price_php)}</td>
                          <td className="p-3 text-right font-bold text-[#00A3A3]">{ps.tenant_count}</td>
                          <td className="p-3 text-right font-mono text-[#F58A26]">{fmt(ps.arr)}</td>
                          <td className="p-3 text-right text-[#5A6570]">{ps.break_even_tenants ?? '-'} tenants</td>
                          <td className="p-3 text-center">
                            <Badge variant={ps.is_active ? 'default' : 'outline'} className={ps.is_active ? 'bg-green-100 text-green-800 border-green-300' : 'text-[#A8B1B9]'}>
                              {ps.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Pricing Config */}
        <TabsContent value="packages" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-[#1A1D21]">Subscription Packages</h2>
            <Button onClick={openNewPkg} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="add-package-btn">
              <Plus className="h-4 w-4 mr-2" />Add Package
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {packages.map(pkg => (
              <Card key={pkg.id} className={`border-[#E2E6EA] ${!pkg.is_active ? 'opacity-60' : ''}`}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        {pkg.name}
                        <Badge variant="outline" className="text-xs">{pkg.code}</Badge>
                      </CardTitle>
                      <CardDescription className="mt-1">{pkg.description}</CardDescription>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditPkg(pkg)} data-testid={`edit-pkg-${pkg.code}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => togglePackageActive(pkg)}>
                        {pkg.is_active ? <Pause className="h-4 w-4 text-amber-500" /> : <Play className="h-4 w-4 text-green-500" />}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-1 mb-3">
                    <span className="text-2xl font-bold text-[#1A1D21]">{fmt(pkg.monthly_price_php)}</span>
                    {pkg.monthly_price_php && <span className="text-sm text-[#5A6570]">/month</span>}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-[#5A6570]">
                    <div><Users className="h-3 w-3 inline mr-1" />{pkg.included_governance_users} users</div>
                    <div><Building2 className="h-3 w-3 inline mr-1" />{pkg.included_entities ?? '∞'} entities</div>
                    <div>Discount: {pkg.annual_discount_percent}% annual</div>
                    <div>Committees: {pkg.max_committees ?? '∞'}</div>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-3">
                    {Object.entries(pkg.feature_flags || {}).filter(([_, v]) => v).map(([k]) => (
                      <Badge key={k} variant="outline" className="text-[10px] bg-[#00A3A3]/5 text-[#00A3A3] border-[#00A3A3]/20">
                        {k.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Cost Assumptions */}
        <TabsContent value="costs" className="space-y-4">
          {settings && (
            <Card className="border-[#E2E6EA]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5 text-[#00A3A3]" />Cost Assumptions & Trial Config</CardTitle>
                <CardDescription>These values drive the financial overview calculations</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Input value={settings.currency} onChange={(e) => setSettings({ ...settings, currency: e.target.value })} className="border-[#E2E6EA]" />
                  </div>
                  <div className="space-y-2">
                    <Label>Exchange Rate (USD to PHP)</Label>
                    <Input type="number" step="0.01" value={settings.exchange_rate_usd_to_php} onChange={(e) => setSettings({ ...settings, exchange_rate_usd_to_php: parseFloat(e.target.value) || 0 })} className="border-[#E2E6EA]" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>One-Time Build Cost (PHP)</Label>
                  <Input type="number" value={settings.one_time_build_cost_php} onChange={(e) => setSettings({ ...settings, one_time_build_cost_php: parseFloat(e.target.value) || 0 })} className="border-[#E2E6EA]" />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Yearly Run Cost Min (PHP)</Label>
                    <Input type="number" value={settings.yearly_run_cost_min_php} onChange={(e) => setSettings({ ...settings, yearly_run_cost_min_php: parseFloat(e.target.value) || 0 })} className="border-[#E2E6EA]" />
                  </div>
                  <div className="space-y-2">
                    <Label>Yearly Run Cost Max (PHP)</Label>
                    <Input type="number" value={settings.yearly_run_cost_max_php} onChange={(e) => setSettings({ ...settings, yearly_run_cost_max_php: parseFloat(e.target.value) || 0 })} className="border-[#E2E6EA]" />
                  </div>
                </div>
                <div className="p-4 bg-[#F8F9FA] rounded-lg">
                  <p className="text-sm text-[#5A6570]">Estimated Yearly Run Cost (midpoint): <strong className="text-[#1A1D21]">{fmt((settings.yearly_run_cost_min_php + settings.yearly_run_cost_max_php) / 2)}</strong></p>
                </div>
                <div className="border-t border-[#E2E6EA] pt-4">
                  <h4 className="font-medium text-[#1A1D21] mb-3">Trial Configuration</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Trial Length (days)</Label>
                      <Input type="number" value={settings.trial_length_days} onChange={(e) => setSettings({ ...settings, trial_length_days: parseInt(e.target.value) || 10 })} className="border-[#E2E6EA]" data-testid="trial-length-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Trial Package Code</Label>
                      <Input value={settings.trial_package_code} onChange={(e) => setSettings({ ...settings, trial_package_code: e.target.value })} className="border-[#E2E6EA]" />
                      <p className="text-xs text-[#5A6570]">Package assigned to new tenants during trial</p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={saveSettings} disabled={saving} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="save-settings-btn">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tenants */}
        <TabsContent value="tenants" className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-semibold text-[#1A1D21]">All Tenants ({tenants.length})</h2>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={openRestore} className="gap-1.5 border-blue-300 text-blue-600 hover:bg-blue-50" data-testid="restore-backup-btn">
                <RotateCcw className="h-4 w-4" />Restore
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportBackup} disabled={exportLoading} className="gap-1.5 border-[#00A3A3] text-[#00A3A3] hover:bg-[#00A3A3]/5" data-testid="export-backup-btn">
                {exportLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <HardDriveDownload className="h-4 w-4" />}
                Export Backup
              </Button>
            </div>
          </div>
          <Card className="border-[#E2E6EA]"><CardContent className="p-0">
            <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]" data-testid="tenants-table">
              <thead>
                <tr className="border-b border-[#E2E6EA] bg-[#F8F9FA]">
                  <th className="text-left p-3 text-xs font-medium text-[#5A6570]">Organization</th>
                  <th className="text-left p-3 text-xs font-medium text-[#5A6570]">Package</th>
                  <th className="text-left p-3 text-xs font-medium text-[#5A6570]">Status</th>
                  <th className="text-right p-3 text-xs font-medium text-[#5A6570]">Users</th>
                  <th className="text-left p-3 text-xs font-medium text-[#5A6570]">Trial End</th>
                  <th className="text-right p-3 text-xs font-medium text-[#5A6570]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map(t => (
                  <tr key={t.id} className="border-b border-[#E2E6EA] last:border-0">
                    <td className="p-3">
                      <p className="font-medium text-[#1A1D21]">{t.name}</p>
                      <p className="text-xs text-[#5A6570]">{t.preset || 'PH Private'}</p>
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className="text-xs">{t.package_code || 'GROWTH'}</Badge>
                    </td>
                    <td className="p-3">
                      <Badge className={`text-xs ${
                        t.subscription_status === 'active' ? 'bg-green-100 text-green-800 border-green-300' :
                        t.subscription_status === 'trial' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                        t.subscription_status === 'suspended' ? 'bg-red-100 text-red-800 border-red-300' :
                        'bg-amber-100 text-amber-800 border-amber-300'
                      }`}>
                        {t.subscription_status || 'trial'}
                      </Badge>
                    </td>
                    <td className="p-3 text-right font-mono">{t.user_count || 0}</td>
                    <td className="p-3 text-sm text-[#5A6570]">
                      {t.trial_end_date ? new Date(t.trial_end_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="h-8" data-testid={`manage-tenant-${t.id}`}>
                            Manage
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem onClick={() => openTenantDetails(t)} data-testid={`view-details-${t.id}`}>
                            <Eye className="h-4 w-4 mr-2" />View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openImpersonate(t)} data-testid={`impersonate-${t.id}`}>
                            <LogIn className="h-4 w-4 mr-2" />Impersonate
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openMakeAdmin(t)} data-testid={`make-admin-${t.id}`}>
                            <Crown className="h-4 w-4 mr-2" />Make Admin
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => openExtendTrial(t)} data-testid={`extend-trial-${t.id}`}>
                            <CalendarPlus className="h-4 w-4 mr-2" />Extend Trial
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openGrantFree(t)} data-testid={`grant-free-${t.id}`}>
                            <Gift className="h-4 w-4 mr-2 text-[#00A3A3]" /><span className="text-[#00A3A3]">Grant 30-Day Free</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openResetLink(t)} data-testid={`send-reset-link-${t.id}`}>
                            <Mail className="h-4 w-4 mr-2" />Send Password Reset
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => openSuspend(t)} data-testid={`suspend-tenant-${t.id}`}>
                            {t.subscription_status === 'suspended' ? (
                              <><Play className="h-4 w-4 mr-2 text-green-600" /><span className="text-green-600">Activate</span></>
                            ) : (
                              <><Pause className="h-4 w-4 mr-2 text-amber-600" /><span className="text-amber-600">Suspend</span></>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openDelete(t)} className="text-red-600 focus:text-red-600" data-testid={`delete-tenant-${t.id}`}>
                            <Trash2 className="h-4 w-4 mr-2" />Delete Account
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {tenants.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-[#5A6570]">No tenants yet</td></tr>
                )}
              </tbody>
            </table>
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* Templates */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <h2 className="text-lg font-semibold text-[#1A1D21]">Default Templates ({templates.length})</h2>
              <p className="text-sm text-[#5A6570]">SEC-recommended and custom templates available to all tenants</p>
            </div>
            <div className="flex items-center gap-2">
              <Select value={tmplFilter} onValueChange={setTmplFilter}>
                <SelectTrigger className="w-[140px] h-9 border-[#E2E6EA]">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {TEMPLATE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={openNewTmpl} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="add-template-btn">
                <Plus className="h-4 w-4 mr-2" />New Template
              </Button>
            </div>
          </div>

          {filteredTemplates.length === 0 ? (
            <Card className="border-[#E2E6EA]">
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto mb-4 text-[#A8B1B9]" />
                <h3 className="text-lg font-medium text-[#1A1D21] mb-2">No templates yet</h3>
                <p className="text-[#5A6570] mb-4">Upload SEC-recommended defaults for tenants to use</p>
                <Button onClick={openNewTmpl} className="bg-[#00A3A3] hover:bg-[#008585]">
                  <Plus className="h-4 w-4 mr-2" />Create First Template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map(tmpl => (
                <Card key={tmpl.id} className={`border-[#E2E6EA] ${!tmpl.is_active ? 'opacity-60' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-[#1A1D21] text-sm truncate">{tmpl.name}</h4>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-[10px] capitalize">{tmpl.template_type?.replace('_', ' ')}</Badge>
                          {tmpl.source === 'SEC_RECOMMENDED' && (
                            <Badge className="text-[10px] bg-[#F58A26]/10 text-[#F58A26] border-[#F58A26]/20">
                              <Shield className="h-2.5 w-2.5 mr-0.5" />SEC
                            </Badge>
                          )}
                          {!tmpl.is_active && <Badge variant="outline" className="text-[10px] text-[#A8B1B9]">Inactive</Badge>}
                        </div>
                      </div>
                      <div className="flex gap-0.5 ml-2">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-[#00A3A3]" title="Preview with sample data" onClick={() => handlePreview(tmpl)} data-testid={`preview-template-${tmpl.id}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditTmpl(tmpl)}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => deleteTmpl(tmpl.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    {tmpl.applicable_meeting_types?.length > 0 && (
                      <div className="flex gap-1 flex-wrap mt-2">
                        {tmpl.applicable_meeting_types.map(mt => (
                          <Badge key={mt} variant="outline" className="text-[10px] bg-[#00A3A3]/5 text-[#00A3A3] border-[#00A3A3]/20 uppercase">{mt}</Badge>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-[#5A6570] mt-2">v{tmpl.version || 1}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <h2 className="text-lg font-semibold text-[#1A1D21]">Subscription Orders ({orders.length})</h2>
              <p className="text-sm text-[#5A6570]">Review and approve/reject manual payment orders</p>
            </div>
            <Select value={orderFilter} onValueChange={setOrderFilter}>
              <SelectTrigger className="w-[160px] h-9 border-[#E2E6EA]">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Orders</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="proof_uploaded">Proof Uploaded</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filteredOrders.length === 0 ? (
            <Card className="border-[#E2E6EA]"><CardContent className="py-12 text-center">
              <ShoppingCart className="h-12 w-12 mx-auto mb-4 text-[#A8B1B9]" />
              <h3 className="text-lg font-medium text-[#1A1D21] mb-2">No orders</h3>
              <p className="text-[#5A6570]">Orders from tenants will appear here</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {filteredOrders.map(order => (
                <Card key={order.id} className={`border-[#E2E6EA] ${
                  order.status === 'proof_uploaded' ? 'border-l-4 border-l-[#F58A26]' :
                  order.status === 'pending' ? 'border-l-4 border-l-blue-400' :
                  order.status === 'approved' ? 'border-l-4 border-l-green-400' :
                  order.status === 'cancelled' ? 'border-l-4 border-l-gray-400' :
                  'border-l-4 border-l-red-400'
                }`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-mono text-xs bg-[#F0F2F4] px-2 py-0.5 rounded">{order.order_id}</span>
                          <Badge className={`text-[10px] ${
                            order.status === 'approved' ? 'bg-green-100 text-green-800' :
                            order.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            order.status === 'cancelled' ? 'bg-gray-100 text-gray-600' :
                            order.status === 'proof_uploaded' ? 'bg-[#F58A26]/10 text-[#F58A26]' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {order.status === 'proof_uploaded' ? 'Proof Uploaded' : order.status}
                          </Badge>
                        </div>
                        <p className="font-medium text-[#1A1D21]">{order.organization_name}</p>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#5A6570] mt-1">
                          <span>{order.package_name} ({order.billing_cycle})</span>
                          <span className="font-semibold text-[#1A1D21]">PHP {Number(order.amount_php).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                          <span>{order.payment_method?.toUpperCase()}</span>
                          <span>{new Date(order.created_at).toLocaleDateString()}</span>
                          {order.user_email && <span className="truncate max-w-[160px]">{order.user_email}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {order.proof_file_path && (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${order.proof_file_path}`}
                            target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 px-2 py-1 bg-[#00A3A3]/10 text-[#00A3A3] rounded text-xs font-medium hover:bg-[#00A3A3]/20"
                            data-testid={`view-proof-${order.id}`}
                          >
                            <Image className="h-3 w-3" />Proof
                          </a>
                        )}
                        {(order.status === 'pending' || order.status === 'proof_uploaded') && (
                          <Button size="sm" className="h-8 bg-[#00A3A3] hover:bg-[#008585]" onClick={() => openReviewOrder(order)} data-testid={`review-order-${order.id}`}>
                            Review
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Package Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editPkg?.id ? 'Edit Package' : 'New Package'}</DialogTitle>
            <DialogDescription>Configure subscription tier pricing and features</DialogDescription>
          </DialogHeader>
          {editPkg && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Code</Label>
                  <Input value={editPkg.code} onChange={(e) => setEditPkg({ ...editPkg, code: e.target.value.toUpperCase() })} disabled={!!editPkg.id} className="border-[#E2E6EA]" />
                </div>
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={editPkg.name} onChange={(e) => setEditPkg({ ...editPkg, name: e.target.value })} className="border-[#E2E6EA]" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={editPkg.description} onChange={(e) => setEditPkg({ ...editPkg, description: e.target.value })} className="border-[#E2E6EA]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Monthly Price (PHP)</Label>
                  <Input type="number" value={editPkg.monthly_price_php ?? ''} onChange={(e) => setEditPkg({ ...editPkg, monthly_price_php: e.target.value ? parseFloat(e.target.value) : null })} className="border-[#E2E6EA]" placeholder="Leave empty for custom" />
                </div>
                <div className="space-y-2">
                  <Label>Annual Discount (%)</Label>
                  <Input type="number" value={editPkg.annual_discount_percent} onChange={(e) => setEditPkg({ ...editPkg, annual_discount_percent: parseFloat(e.target.value) || 0 })} className="border-[#E2E6EA]" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Users</Label>
                  <Input type="number" value={editPkg.included_governance_users} onChange={(e) => setEditPkg({ ...editPkg, included_governance_users: parseInt(e.target.value) || 0 })} className="border-[#E2E6EA]" />
                </div>
                <div className="space-y-2">
                  <Label>Entities</Label>
                  <Input type="number" value={editPkg.included_entities ?? ''} onChange={(e) => setEditPkg({ ...editPkg, included_entities: e.target.value ? parseInt(e.target.value) : null })} className="border-[#E2E6EA]" placeholder="∞" />
                </div>
                <div className="space-y-2">
                  <Label>Committees</Label>
                  <Input type="number" value={editPkg.max_committees ?? ''} onChange={(e) => setEditPkg({ ...editPkg, max_committees: e.target.value ? parseInt(e.target.value) : null })} className="border-[#E2E6EA]" placeholder="∞" />
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Feature Flags</Label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.keys(editPkg.feature_flags || {}).map(key => (
                    <div key={key} className="flex items-center justify-between p-2 rounded border border-[#E2E6EA]">
                      <span className="text-xs text-[#3A3F45] capitalize">{key.replace(/_/g, ' ')}</span>
                      <Switch
                        checked={editPkg.feature_flags[key]}
                        onCheckedChange={(v) => setEditPkg({ ...editPkg, feature_flags: { ...editPkg.feature_flags, [key]: v } })}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={savePackage} disabled={saving} className="bg-[#00A3A3] hover:bg-[#008585]">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={tmplDialog} onOpenChange={setTmplDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editTmpl?.id ? 'Edit Template' : 'New Default Template'}</DialogTitle>
            <DialogDescription>Create or edit a landlord-level template available to all tenants</DialogDescription>
          </DialogHeader>
          {editTmpl && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Template Name</Label>
                  <Input value={editTmpl.name} onChange={(e) => setEditTmpl({ ...editTmpl, name: e.target.value })} className="border-[#E2E6EA]" placeholder="e.g., SEC Board Notice – Default" data-testid="template-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={editTmpl.template_type} onValueChange={(v) => setEditTmpl({ ...editTmpl, template_type: v })}>
                    <SelectTrigger className="border-[#E2E6EA]" data-testid="template-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEMPLATE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Source</Label>
                  <Select value={editTmpl.source} onValueChange={(v) => setEditTmpl({ ...editTmpl, source: v })}>
                    <SelectTrigger className="border-[#E2E6EA]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SEC_RECOMMENDED">SEC Recommended</SelectItem>
                      <SelectItem value="LANDLORD_CUSTOM">Landlord Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Applicable Meeting Types</Label>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {MEETING_TYPES.map(mt => {
                      const selected = editTmpl.applicable_meeting_types?.includes(mt.value);
                      return (
                        <Button
                          key={mt.value}
                          type="button"
                          variant={selected ? 'default' : 'outline'}
                          size="sm"
                          className={`text-xs h-7 ${selected ? 'bg-[#00A3A3] hover:bg-[#008585]' : ''}`}
                          onClick={() => {
                            const types = editTmpl.applicable_meeting_types || [];
                            setEditTmpl({
                              ...editTmpl,
                              applicable_meeting_types: selected
                                ? types.filter(t => t !== mt.value)
                                : [...types, mt.value],
                            });
                          }}
                        >
                          {mt.label}
                        </Button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-[#5A6570]">Leave empty for generic (all meeting types)</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={editTmpl.is_active} onCheckedChange={(v) => setEditTmpl({ ...editTmpl, is_active: v })} />
                <Label>Active</Label>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Template Body</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".docx,.html,.htm"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="template-file-upload"
                      data-testid="template-file-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs h-7 border-[#F58A26]/30 text-[#F58A26] hover:bg-[#F58A26]/5"
                      onClick={() => document.getElementById('template-file-upload').click()}
                      disabled={uploading}
                    >
                      {uploading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
                      Upload .docx / .html
                    </Button>
                  </div>
                </div>
                <RichTextEditor
                  content={editTmpl.body_html}
                  onChange={(html) => setEditTmpl({ ...editTmpl, body_html: html })}
                  placeholders={editTmpl.placeholders || COMMON_PLACEHOLDERS}
                  placeholder="Write your template content here, or upload a .docx / .html file..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTmplDialog(false)}>Cancel</Button>
            <Button onClick={saveTmpl} disabled={saving || !editTmpl?.name || !editTmpl?.template_type} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="save-template-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Template Dialog */}
      <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-[#00A3A3]" />
              Template Preview
            </DialogTitle>
            <DialogDescription>
              {previewName} — rendered with sample Philippine corporate data. Highlighted values are placeholder fills.
            </DialogDescription>
          </DialogHeader>
          <div className="border border-[#E2E6EA] rounded-lg bg-white p-6 mt-2">
            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
              data-testid="template-preview-content"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Order Dialog */}
      <Dialog open={reviewDialog} onOpenChange={setReviewDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review Order</DialogTitle>
            <DialogDescription>Approve or reject this subscription payment</DialogDescription>
          </DialogHeader>
          {reviewOrder && (
            <div className="space-y-4 py-2">
              <div className="bg-[#FAFBFC] p-4 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#5A6570]">Order ID</span><span className="font-mono font-medium">{reviewOrder.order_id}</span></div>
                <div className="flex justify-between"><span className="text-[#5A6570]">Organization</span><span className="font-medium">{reviewOrder.organization_name}</span></div>
                <div className="flex justify-between"><span className="text-[#5A6570]">Package</span><span className="font-medium">{reviewOrder.package_name}</span></div>
                <div className="flex justify-between"><span className="text-[#5A6570]">Amount</span><span className="font-bold text-[#F58A26]">PHP {Number(reviewOrder.amount_php).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between"><span className="text-[#5A6570]">Payment</span><span className="font-medium uppercase">{reviewOrder.payment_method}</span></div>
                {reviewOrder.contact_number && (
                  <div className="flex justify-between"><span className="text-[#5A6570]">Contact</span><span className="font-medium">{reviewOrder.contact_number}</span></div>
                )}
              </div>

              {/* Proof of Payment Section */}
              {reviewOrder.proof_file_path ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-[#1A1D21]">Proof of Payment</h4>
                    <div className="flex items-center gap-1">
                      <a
                        href={getProofUrl(reviewOrder.proof_file_path)}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 bg-[#00A3A3]/10 text-[#00A3A3] rounded text-xs font-medium hover:bg-[#00A3A3]/20 transition-colors"
                        data-testid="download-proof-btn"
                      >
                        <Download className="h-3 w-3" />Download
                      </a>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteProof(reviewOrder.id)}
                        data-testid="delete-proof-btn"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />Delete
                      </Button>
                    </div>
                  </div>
                  {isProofImage(reviewOrder.proof_file_path) ? (
                    <div className="border border-[#E2E6EA] rounded-lg overflow-hidden bg-white">
                      <img
                        src={getProofUrl(reviewOrder.proof_file_path)}
                        alt="Proof of payment"
                        className="w-full max-h-[300px] object-contain"
                        data-testid="proof-image-preview"
                      />
                    </div>
                  ) : (
                    <div className="border border-[#E2E6EA] rounded-lg p-4 bg-white flex items-center gap-3">
                      <FileText className="h-8 w-8 text-[#A8B1B9]" />
                      <div>
                        <p className="text-sm font-medium text-[#1A1D21]">PDF Document</p>
                        <a href={getProofUrl(reviewOrder.proof_file_path)} target="_blank" rel="noopener noreferrer" className="text-xs text-[#00A3A3] hover:underline">
                          Open in new tab
                        </a>
                      </div>
                    </div>
                  )}
                  {reviewOrder.proof_uploaded_at && (
                    <p className="text-xs text-[#5A6570]">Uploaded: {new Date(reviewOrder.proof_uploaded_at).toLocaleString()}</p>
                  )}
                </div>
              ) : (
                <div className="border-2 border-dashed border-[#E2E6EA] rounded-lg p-6 text-center">
                  <Ban className="h-8 w-8 mx-auto mb-2 text-[#A8B1B9]" />
                  <p className="text-sm text-[#5A6570]">No proof of payment uploaded</p>
                  <p className="text-xs text-[#A8B1B9] mt-1">Order will be auto-cancelled if no proof is uploaded within 2 hours</p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Admin Notes (optional)</Label>
                <Input value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} placeholder="Add a note..." className="border-[#E2E6EA]" data-testid="review-notes-input" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReviewDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRejectOrder} disabled={saving} data-testid="reject-order-btn">
              <XCircle className="h-4 w-4 mr-2" />Reject
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleApproveOrder} disabled={saving} data-testid="approve-order-btn">
              <CheckCheck className="h-4 w-4 mr-2" />Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Tenant Management Modals ============ */}

      {/* View Tenant Details Dialog */}
      <Dialog open={tenantDetailDialog} onOpenChange={setTenantDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#00A3A3]" />Tenant Details
            </DialogTitle>
            <DialogDescription>Full details for this tenant organization</DialogDescription>
          </DialogHeader>
          {tenantDetailLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" /></div>
          ) : tenantDetail && (
            <div className="space-y-4 py-2">
              <div className="bg-[#FAFBFC] p-4 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#5A6570]">Organization</span><span className="font-semibold text-[#1A1D21]">{tenantDetail.name}</span></div>
                <div className="flex justify-between"><span className="text-[#5A6570]">Package</span><Badge variant="outline" className="text-xs">{tenantDetail.package_code || 'GROWTH'}</Badge></div>
                <div className="flex justify-between"><span className="text-[#5A6570]">Status</span>
                  <Badge className={`text-xs ${
                    tenantDetail.subscription_status === 'active' ? 'bg-green-100 text-green-800' :
                    tenantDetail.subscription_status === 'trial' ? 'bg-blue-100 text-blue-800' :
                    tenantDetail.subscription_status === 'suspended' ? 'bg-red-100 text-red-800' :
                    'bg-amber-100 text-amber-800'
                  }`}>{tenantDetail.subscription_status || 'trial'}</Badge>
                </div>
                <div className="flex justify-between"><span className="text-[#5A6570]">Preset</span><span>{tenantDetail.preset || 'PH Private'}</span></div>
                {tenantDetail.trial_end_date && (
                  <div className="flex justify-between"><span className="text-[#5A6570]">Trial End</span><span>{new Date(tenantDetail.trial_end_date).toLocaleDateString()}</span></div>
                )}
                <div className="flex justify-between"><span className="text-[#5A6570]">Total Users</span><span className="font-mono">{tenantDetail.users?.length || 0}</span></div>
                <div className="flex justify-between"><span className="text-[#5A6570]">Meetings Held</span><span className="font-mono">{tenantDetail.meeting_count || 0}</span></div>
                {tenantDetail.created_at && (
                  <div className="flex justify-between"><span className="text-[#5A6570]">Created</span><span>{new Date(tenantDetail.created_at).toLocaleDateString()}</span></div>
                )}
              </div>

              {tenantDetail.users?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-[#1A1D21] mb-2">Users ({tenantDetail.users.length})</h4>
                  <div className="border border-[#E2E6EA] rounded-lg overflow-hidden">
                    <table className="w-full text-sm" data-testid="tenant-users-table">
                      <thead>
                        <tr className="bg-[#F8F9FA] border-b border-[#E2E6EA]">
                          <th className="text-left p-2.5 text-xs font-medium text-[#5A6570]">Name</th>
                          <th className="text-left p-2.5 text-xs font-medium text-[#5A6570]">Email</th>
                          <th className="text-left p-2.5 text-xs font-medium text-[#5A6570]">Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tenantDetail.users.map(u => (
                          <tr key={u.id} className="border-b border-[#E2E6EA] last:border-0">
                            <td className="p-2.5 font-medium text-[#1A1D21]">{u.full_name}</td>
                            <td className="p-2.5 text-[#5A6570]">{u.email}</td>
                            <td className="p-2.5">
                              <Badge variant="outline" className="text-[10px] capitalize">{u.role?.replace(/_/g, ' ')}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTenantDetailDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impersonate Tenant Dialog */}
      <AlertDialog open={impersonateDialog} onOpenChange={setImpersonateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <LogIn className="h-5 w-5 text-[#00A3A3]" />Impersonate Tenant
            </AlertDialogTitle>
            <AlertDialogDescription>
              You will be logged in as the primary user of <strong>{impersonateTenant?.name}</strong>. 
              You can return to your landlord account by logging out.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleImpersonate} disabled={actionLoading} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="confirm-impersonate-btn">
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogIn className="h-4 w-4 mr-2" />}
              Impersonate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Make Admin Dialog */}
      <Dialog open={makeAdminDialog} onOpenChange={setMakeAdminDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-[#F58A26]" />Promote to Platform Admin
            </DialogTitle>
            <DialogDescription>
              Select a user from <strong>{makeAdminTenant?.name}</strong> to promote to the landlord (platform admin) role.
            </DialogDescription>
          </DialogHeader>
          {makeAdminUsers.length === 0 ? (
            <div className="py-8 text-center text-[#5A6570]">
              <Users className="h-8 w-8 mx-auto mb-2 text-[#A8B1B9]" />
              <p className="text-sm">No users found in this tenant</p>
            </div>
          ) : (
            <div className="space-y-2 py-2">
              {makeAdminUsers.map(u => (
                <div key={u.id} className="flex items-center justify-between p-3 rounded-lg border border-[#E2E6EA] hover:bg-[#F8F9FA] transition-colors">
                  <div>
                    <p className="font-medium text-sm text-[#1A1D21]">{u.full_name}</p>
                    <p className="text-xs text-[#5A6570]">{u.email}</p>
                    <Badge variant="outline" className="text-[10px] capitalize mt-1">{u.role?.replace(/_/g, ' ')}</Badge>
                  </div>
                  {u.role === 'landlord' ? (
                    <Badge className="bg-[#F58A26]/10 text-[#F58A26] text-xs">Already Admin</Badge>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => handleMakeAdmin(u.id)} disabled={actionLoading} className="border-[#F58A26] text-[#F58A26] hover:bg-[#F58A26]/5" data-testid={`promote-user-${u.id}`}>
                      {actionLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Crown className="h-3 w-3 mr-1" />}
                      Promote
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMakeAdminDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Password Reset Link Dialog */}
      <Dialog open={resetLinkDialog} onOpenChange={(open) => { setResetLinkDialog(open); if (!open) setResetLinkResult(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#00A3A3]" />Send Password Reset Link
            </DialogTitle>
            <DialogDescription>
              Select a user from <strong>{resetLinkTenant?.name}</strong> to send a password reset email.
            </DialogDescription>
          </DialogHeader>
          {resetLinkResult ? (
            <div className="space-y-4 py-2">
              <div className={`border rounded-lg p-4 ${resetLinkResult.email_sent ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center gap-2 mb-3">
                  {resetLinkResult.email_sent ? (
                    <><CheckCircle className="h-5 w-5 text-green-600" /><h4 className="font-semibold text-green-800">Reset Email Sent</h4></>
                  ) : (
                    <><AlertTriangle className="h-5 w-5 text-amber-600" /><h4 className="font-semibold text-amber-800">Reset Link Generated (Email Not Sent)</h4></>
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[#5A6570]">Sent To</span><span className="font-medium">{resetLinkResult.email}</span></div>
                  <div className="flex justify-between"><span className="text-[#5A6570]">User</span><span className="font-medium">{resetLinkResult.user_name}</span></div>
                  <div className="flex justify-between"><span className="text-[#5A6570]">Expires In</span><span className="font-medium">{resetLinkResult.expires_in_minutes} minutes</span></div>
                  <div className="mt-3">
                    <p className="text-xs text-[#5A6570] mb-1">Reset Code</p>
                    <div className="flex items-center gap-2">
                      <code className="bg-white px-3 py-1.5 rounded border font-mono text-lg tracking-widest font-bold text-[#00A3A3]" data-testid="reset-code">{resetLinkResult.code}</code>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => {
                        navigator.clipboard.writeText(resetLinkResult.code);
                        toast.success('Code copied');
                      }} data-testid="copy-reset-code-btn">
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2">
                    <p className="text-xs text-[#5A6570] mb-1">Reset Link</p>
                    <div className="flex items-center gap-2">
                      <code className="bg-white px-2 py-1 rounded border text-xs break-all flex-1" data-testid="reset-link">{resetLinkResult.reset_link}</code>
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => {
                        navigator.clipboard.writeText(resetLinkResult.reset_link);
                        toast.success('Link copied');
                      }} data-testid="copy-reset-link-btn">
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              {!resetLinkResult.email_sent && (
                <p className="text-xs text-amber-600">Email could not be sent. Please share the reset link or code with the user manually.</p>
              )}
            </div>
          ) : resetLinkUsers.length === 0 ? (
            <div className="py-8 text-center text-[#5A6570]">
              <Users className="h-8 w-8 mx-auto mb-2 text-[#A8B1B9]" />
              <p className="text-sm">No users found in this tenant</p>
            </div>
          ) : (
            <div className="space-y-2 py-2">
              {resetLinkUsers.map(u => (
                <div key={u.id} className="flex items-center justify-between p-3 rounded-lg border border-[#E2E6EA] hover:bg-[#F8F9FA] transition-colors">
                  <div>
                    <p className="font-medium text-sm text-[#1A1D21]">{u.full_name}</p>
                    <p className="text-xs text-[#5A6570]">{u.email}</p>
                    <Badge variant="outline" className="text-[10px] capitalize mt-1">{u.role?.replace(/_/g, ' ')}</Badge>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleSendResetLink(u.id)} disabled={actionLoading} className="border-[#00A3A3] text-[#00A3A3] hover:bg-[#00A3A3]/5" data-testid={`send-reset-user-${u.id}`}>
                    {actionLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3 mr-1" />}
                    Send Reset
                  </Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetLinkDialog(false); setResetLinkResult(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend Trial Dialog */}
      <AlertDialog open={extendTrialDialog} onOpenChange={setExtendTrialDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CalendarPlus className="h-5 w-5 text-[#00A3A3]" />Extend Trial
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <span className="block">Extend the trial period for <strong>{extendTrialTenant?.name}</strong>.</span>
              <span className="block text-sm">Current status: <Badge variant="outline" className="text-xs ml-1">{extendTrialTenant?.subscription_status || 'trial'}</Badge></span>
              {extendTrialTenant?.trial_end_date && (
                <span className="block text-sm">Current trial end: {new Date(extendTrialTenant.trial_end_date).toLocaleDateString()}</span>
              )}
              <div className="flex items-center gap-3 pt-1">
                <Label className="text-sm whitespace-nowrap">Days to add:</Label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={extendTrialDays}
                  onChange={(e) => setExtendTrialDays(parseInt(e.target.value) || 30)}
                  className="w-24 border-[#E2E6EA]"
                  data-testid="extend-trial-days-input"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleExtendTrial} disabled={actionLoading} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="confirm-extend-trial-btn">
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarPlus className="h-4 w-4 mr-2" />}
              Extend by {extendTrialDays} Days
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Grant 30-Day Free Subscription Dialog */}
      <AlertDialog open={grantFreeDialog} onOpenChange={setGrantFreeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-[#00A3A3]" />Grant 30-Day Free Subscription
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will activate the subscription for <strong>{grantFreeTenant?.name}</strong> for 30 days at no charge.
              The tenant's status will change to <Badge variant="outline" className="text-xs mx-1 bg-green-100 text-green-800">active</Badge> immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleGrantFree} disabled={actionLoading} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="confirm-grant-free-btn">
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Gift className="h-4 w-4 mr-2" />}
              Grant Free Subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Suspend / Activate Confirmation Dialog */}
      <AlertDialog open={suspendDialog} onOpenChange={setSuspendDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {suspendTenant?.subscription_status === 'suspended' ? (
                <><Play className="h-5 w-5 text-green-600" />Activate Tenant</>
              ) : (
                <><AlertTriangle className="h-5 w-5 text-amber-500" />Suspend Tenant</>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {suspendTenant?.subscription_status === 'suspended'
                ? <>Are you sure you want to reactivate <strong>{suspendTenant?.name}</strong>? Their users will regain access.</>
                : <>Are you sure you want to suspend <strong>{suspendTenant?.name}</strong>? All users in this organization will lose access until reactivated.</>
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSuspendConfirm}
              disabled={actionLoading}
              className={suspendTenant?.subscription_status === 'suspended' ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'}
              data-testid="confirm-suspend-btn"
            >
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {suspendTenant?.subscription_status === 'suspended' ? 'Activate' : 'Suspend'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Tenant Confirmation Dialog */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />Delete Tenant Account
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <span className="block">This will permanently delete <strong>{deleteTenantState?.name}</strong> and all associated data including users, meetings, templates, and records. This action cannot be undone.</span>
              <span className="block text-sm">Type the organization name to confirm:</span>
              <Input
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder={deleteTenantState?.name}
                className="border-red-200 focus:border-red-400"
                data-testid="delete-confirm-input"
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={actionLoading || deleteConfirmName !== deleteTenantState?.name}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-tenant-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Tenants from Backup Dialog */}
      <Dialog open={restoreDialog} onOpenChange={setRestoreDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-[#1A1D21]">Restore Tenants from Backup</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Restore Mode */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-[#5A6570]">Restore Mode</p>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="restore-mode-merge">
                  <input type="radio" name="restoreMode" value="merge" checked={restoreMode === 'merge'} onChange={() => setRestoreMode('merge')}
                    className="w-4 h-4 accent-blue-600" />
                  <span className="text-sm"><strong>Merge</strong> – Add new tenants, update existing</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="restore-mode-replace">
                  <input type="radio" name="restoreMode" value="replace" checked={restoreMode === 'replace'} onChange={() => setRestoreMode('replace')}
                    className="w-4 h-4 accent-red-600" />
                  <span className="text-sm"><strong className="text-red-600">Replace</strong> – Delete all tenants first</span>
                </label>
              </div>
            </div>

            {/* Restore from File */}
            <div className="border-2 border-dashed border-[#E2E6EA] rounded-lg p-5 space-y-2">
              <div className="flex items-center gap-2">
                <FileUp className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="font-semibold text-sm text-[#1A1D21]">Restore from File</p>
                  <p className="text-xs text-[#5A6570]">Upload a previously downloaded JSON backup file</p>
                </div>
              </div>
              <label className="flex items-center justify-center gap-2 py-3 px-4 border border-[#E2E6EA] rounded-lg cursor-pointer hover:bg-[#F8F9FA] transition-colors" data-testid="select-backup-file-btn">
                <UploadCloud className="h-4 w-4 text-[#5A6570]" />
                <span className="text-sm font-medium text-[#5A6570]">
                  {restoreLoading ? 'Restoring...' : 'Select Backup File'}
                </span>
                <input type="file" accept=".json" className="hidden" onChange={handleRestoreFromFile} disabled={restoreLoading} />
              </label>
            </div>

            {/* Restore from Database */}
            <div className="border border-[#E2E6EA] rounded-lg overflow-hidden">
              <div className="flex items-center gap-2 p-4 bg-[#F8F9FA] border-b border-[#E2E6EA]">
                <Database className="h-5 w-5 text-[#00A3A3]" />
                <div>
                  <p className="font-semibold text-sm text-[#1A1D21]">Restore from Database</p>
                  <p className="text-xs text-[#5A6570]">Select from previously saved backups</p>
                </div>
              </div>
              <div className="max-h-[260px] overflow-y-auto">
                {backupsLoading ? (
                  <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#00A3A3]" /></div>
                ) : backupsList.length === 0 ? (
                  <div className="py-8 text-center text-[#5A6570] text-sm">No saved backups yet. Export a backup first.</div>
                ) : (
                  backupsList.map(b => (
                    <div key={b.id} className="flex items-center justify-between p-4 border-b border-[#E2E6EA] last:border-0 hover:bg-[#FAFBFC] transition-colors">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm text-[#1A1D21] truncate" data-testid={`backup-name-${b.id}`}>{b.name}</p>
                        <p className="text-xs text-[#5A6570]">
                          {new Date(b.created_at).toLocaleString()} &bull; {b.tenant_count} tenants &bull; {b.user_count} users
                        </p>
                        <p className="text-xs text-[#A8B1B9]">by {b.created_by}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-3 shrink-0">
                        <Button size="sm" onClick={() => handleRestoreFromDb(b.id)} disabled={restoreLoading}
                          className="bg-green-600 hover:bg-green-700 text-white h-8" data-testid={`restore-from-db-${b.id}`}>
                          {restoreLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Restore'}
                        </Button>
                        <Button size="icon" variant="outline" onClick={() => handleDeleteBackup(b.id)}
                          className="h-8 w-8 border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600" data-testid={`delete-backup-${b.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreDialog(false)} className="w-full" data-testid="cancel-restore-btn">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandlordDashboard;
